import { useState } from 'react';
import aiService from '../../../services/aiService';
import { PatientInfo, SubtestScores, CompositeScores } from '../types';

export const usePatientData = (setLoading: (loading: boolean) => void, setLoadingMessage: (msg: string) => void) => {
  // Dane pacjenta
  const [patientInfo, setPatientInfo] = useState<PatientInfo>({
    name: '',
    birthDate: '',
    testDate: '',
    examiner: '',
    institution: ''
  });

  // Wyniki przeliczone podtestów
  const [subtestScores, setSubtestScores] = useState<SubtestScores>({
    block_design: '',
    similarities: '',
    matrix_reasoning: '',
    digit_span: '',
    coding: '',
    vocabulary: '',
    figure_weights: '',
    visual_puzzles: '',
    picture_span: '',
    symbol_search: ''
  });

  // Wskaźniki złożone
  const [compositeScores, setCompositeScores] = useState<CompositeScores>({
    vci: '',
    vsi: '',
    fri: '',
    wmi: '',
    psi: '',
    fsiq: ''
  });

  const loadTestData = () => {
    const testData = {
      patient_info: {
        name: "Jan Kowalski",
        birth_date: "2010-03-15",
        test_date: "2024-11-20",
        examiner: "dr Anna Nowak",
        institution: "Poradnia Psychologiczno-Pedagogiczna nr 1"
      },
      subtest_scores: {
        block_design: "9",
        similarities: "4",
        matrix_reasoning: "7",
        digit_span: "4",
        coding: "6",
        vocabulary: "6",
        figure_weights: "1",
        visual_puzzles: "11",
        picture_span: "9",
        symbol_search: "4"
      },
      composite_scores: {
        vci: "73",
        vsi: "100",
        fri: "68",
        wmi: "79",
        psi: "70",
        fsiq: "68"
      }
    };

    // Load patient info
    if (testData.patient_info) {
      setPatientInfo({
        name: testData.patient_info.name || '',
        birthDate: testData.patient_info.birth_date || '',
        testDate: testData.patient_info.test_date || '',
        examiner: testData.patient_info.examiner || '',
        institution: testData.patient_info.institution || ''
      });
    }

    // Load subtest scores
    if (testData.subtest_scores) {
      setSubtestScores({
        block_design: testData.subtest_scores.block_design || '',
        similarities: testData.subtest_scores.similarities || '',
        matrix_reasoning: testData.subtest_scores.matrix_reasoning || '',
        digit_span: testData.subtest_scores.digit_span || '',
        coding: testData.subtest_scores.coding || '',
        vocabulary: testData.subtest_scores.vocabulary || '',
        figure_weights: testData.subtest_scores.figure_weights || '',
        visual_puzzles: testData.subtest_scores.visual_puzzles || '',
        picture_span: testData.subtest_scores.picture_span || '',
        symbol_search: testData.subtest_scores.symbol_search || ''
      });
    }

    // Load composite scores
    if (testData.composite_scores) {
      setCompositeScores({
        vci: testData.composite_scores.vci || '',
        vsi: testData.composite_scores.vsi || '',
        fri: testData.composite_scores.fri || '',
        wmi: testData.composite_scores.wmi || '',
        psi: testData.composite_scores.psi || '',
        fsiq: testData.composite_scores.fsiq || ''
      });
    }
  };

  const handlePatientInfoChange = (field: keyof PatientInfo, value: string) => {
    setPatientInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubtestScoreChange = (subtest: string, value: string) => {
    setSubtestScores(prev => ({
      ...prev,
      [subtest]: value
    }));
  };

  const handleCompositeScoreChange = (composite: string, value: string) => {
    setCompositeScores(prev => ({
      ...prev,
      [composite]: value
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Proszę wybrać plik obrazu (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Plik jest za duży. Maksymalny rozmiar to 5MB');
      return;
    }

    setLoading(true);
    setLoadingMessage('Analizuję obraz z OCR...');

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result as string;

        try {
          const result = await aiService.analyzeWISCVCard(base64);

          // Update subtest scores
          if (result.subtestScores) {
            setSubtestScores(prev => ({
              ...prev,
              ...result.subtestScores
            }));
          }

          // Update composite scores
          if (result.compositeScores) {
            setCompositeScores(prev => ({
              ...prev,
              ...result.compositeScores
            }));
          }

          alert('✅ Pomyślnie wypełniono wyniki z obrazu!');
        } catch (error: any) {
          console.error('OCR analysis failed:', error);
          alert(`Błąd analizy OCR: ${error.message}`);
        } finally {
          setLoading(false);
          setLoadingMessage('');
        }
      };

      reader.readAsDataURL(file);
    } catch (error: any) {
      console.error('File upload failed:', error);
      alert(`Błąd wczytywania pliku: ${error.message}`);
      setLoading(false);
      setLoadingMessage('');
    }
  };

  return {
    patientInfo,
    subtestScores,
    compositeScores,
    loadTestData,
    handlePatientInfoChange,
    handleSubtestScoreChange,
    handleCompositeScoreChange,
    handleImageUpload
  };
};
